import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Divider,
  IconButton,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  GetProductById,
  SaveProduct,
  UpdateProduct,
} from "../../../services/InventoryService";
import { GetErrorMessage } from "../../../services/InventoryTrackerService";
import { GetVendors } from "../../../services/GeneralMaintenanceService";
import {
  inventoryOrderLevelList,
} from "../../common/statusLists";
import useDropdownList from "../../common/useDropdownList";
import useInventoryLocationList from "../../common/useInventoryLocationList";
import { DROPDOWN_LIST_TYPES } from "../../../services/DropdownListService";
import CommonLoader from "../../common/CommonLoader";
import { ReorderStatusChip } from "../tracker/ReorderReport";

const AddEditProduct = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const isEdit = Boolean(idParam);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [vendorRecordId, setVendorRecordId] = useState("");
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  // ENB-584: an unknown code scanned in the Inventory Tracker opens Add Product with it as the item number.
  const [sku, setSku] = useState(routeLocation.state?.sku || "");
  const [upc, setUpc] = useState("");
  const [category, setCategory] = useState("");
  // Admin-managed list (Maintenance > Dropdown Lists); a saved category no longer in the list is kept.
  const categoryOptions = useDropdownList(DROPDOWN_LIST_TYPES.PRODUCT_CATEGORY, category);
  const [orderLevel, setOrderLevel] = useState("");
  const [leadTimeDays, setLeadTimeDays] = useState("");
  const [cost, setCost] = useState("");
  // ENB-584 reorder: manual burn rate, or switch to the calculated 60-day rate once there is history.
  const [burnRatePerWeek, setBurnRatePerWeek] = useState("");
  const [useCalculatedBurnRate, setUseCalculatedBurnRate] = useState(false);
  const [reorder, setReorder] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [variants, setVariants] = useState([]);
  const [locations, setLocations] = useState([]);
  // ENB-584: Inventory Locations (Warehouse > Rack > Shelf) as full paths; saved locations no longer active are kept.
  const locationOptions = useInventoryLocationList(
    locations.map((location) => location.locationName)
  );
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    fetchVendors();
    if (isEdit) fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  const fetchVendors = async () => {
    try {
      const response = await GetVendors();
      setVendors(response?.results ?? []);
    } catch {
      NotificationManager.error("Error loading vendors");
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await GetProductById(idParam);
      const p = response?.results ?? response;
      if (!p) return;
      populateFields(p);
    } catch {
      NotificationManager.error("Error loading product record");
    } finally {
      setLoading(false);
    }
  };

  const populateFields = (p) => {
    setVendorRecordId(p.vendorRecordId ?? "");
    setProductName(p.productName || "");
    setDescription(p.description || "");
    setSku(p.sku || "");
    setUpc(p.upc || "");
    setCategory(p.category || "");
    setOrderLevel(p.orderLevel || "");
    setLeadTimeDays(p.leadTimeDays ?? "");
    setCost(p.cost ?? "");
    setBurnRatePerWeek(p.burnRatePerWeek ?? "");
    setUseCalculatedBurnRate(Boolean(p.useCalculatedBurnRate));
    setReorder(p.reorder || null);
    setIsActive(p.isActive === undefined ? true : p.isActive);
    setVariants(p.variants || []);
    setLocations(p.locations || []);
  };

  // Variants

  const handleAddVariant = () => {
    setVariants([
      ...variants,
      { id: 0, variantName: "", sku: "", upc: "", cost: "", isActive: true },
    ]);
  };

  const handleVariantChange = (index, field, value) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  const handleRemoveVariant = (index) => {
    const removed = variants[index];
    setVariants(variants.filter((_, i) => i !== index));
    // Stock rows pointing at a removed variant would be orphaned, so drop them too.
    setLocations(
      locations.filter(
        (location) =>
          !(
            (removed.id > 0 && location.productVariantId === removed.id) ||
            (removed.id === 0 &&
              removed.variantName &&
              location.variantName === removed.variantName)
          )
      )
    );
  };

  // Locations - the only place stock quantity is held

  const handleAddLocation = () => {
    setLocations([
      ...locations,
      {
        id: 0,
        productVariantId: null,
        variantName: "",
        locationName: "",
        lotNumber: "",
        expirationDate: "",
        stockLevel: 0,
        notes: "",
      },
    ]);
  };

  const handleLocationChange = (index, field, value) => {
    const updated = [...locations];
    updated[index] = { ...updated[index], [field]: value };

    // Keep the variant id and name in step - a brand new variant has no id yet,
    // so the API resolves it from the name.
    if (field === "variantName") {
      const match = variants.find((variant) => variant.variantName === value);
      updated[index].productVariantId = match && match.id > 0 ? match.id : null;
    }

    setLocations(updated);
  };

  const handleRemoveLocation = (index) => {
    setLocations(locations.filter((_, i) => i !== index));
  };

  const totalStock = locations.reduce(
    (sum, location) => sum + (Number(location.stockLevel) || 0),
    0
  );

  const buildPayload = () => ({
    id: isEdit ? parseInt(idParam, 10) : 0,
    vendorRecordId: parseInt(vendorRecordId, 10),
    productName,
    description,
    sku,
    upc,
    category,
    orderLevel,
    leadTimeDays: leadTimeDays !== "" ? parseInt(leadTimeDays, 10) : null,
    cost: cost !== "" ? parseFloat(cost) : null,
    burnRatePerWeek: burnRatePerWeek !== "" ? parseFloat(burnRatePerWeek) : null,
    useCalculatedBurnRate,
    isActive,
    variants: variants
      .filter((variant) => variant.variantName?.trim())
      .map((variant) => ({
        id: variant.id || 0,
        productId: isEdit ? parseInt(idParam, 10) : 0,
        variantName: variant.variantName.trim(),
        sku: variant.sku,
        upc: variant.upc,
        cost:
          variant.cost !== "" && variant.cost !== null
            ? parseFloat(variant.cost)
            : null,
        isActive: variant.isActive === undefined ? true : variant.isActive,
      })),
    locations: locations
      .filter((location) => location.locationName?.trim())
      .map((location) => ({
        id: location.id || 0,
        productId: isEdit ? parseInt(idParam, 10) : 0,
        productVariantId: location.productVariantId || null,
        variantName: location.variantName,
        locationName: location.locationName.trim(),
        lotNumber: location.lotNumber?.trim() || null,
        expirationDate: location.expirationDate ? location.expirationDate.split("T")[0] : null,
        stockLevel: Number(location.stockLevel) || 0,
        notes: location.notes,
      })),
  });

  const handleSubmit = async () => {
    if (!productName?.trim()) {
      NotificationManager.error("Product name is required");
      return;
    }

    if (!vendorRecordId) {
      NotificationManager.error("Vendor is required");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await UpdateProduct(buildPayload());
        NotificationManager.success("Product updated successfully");
      } else {
        await SaveProduct(buildPayload());
        NotificationManager.success("Product saved successfully");
      }
      navigate("/vlp/inventory-products");
    } catch (error) {
      // e.g. a duplicate item number
      NotificationManager.error(await GetErrorMessage(error, "Error saving product"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate("/vlp/inventory-products");
    }
  };

  const tf = (label, value, setter, extra = {}) => (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
      fullWidth
      {...extra}
    />
  );

  const select = (label, value, setter, options, extra = {}) => (
    <TextField
      size="small"
      select
      label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
      fullWidth
      {...extra}
    >
      {options}
    </TextField>
  );

  return (
    <div className="vlp-component-container">
      <span className="title">{isEdit ? "Edit Product" : "Add Product"}</span>
      <hr />

      <Paper elevation={2} sx={{ p: 2 }}>
        <Grid container spacing={1.5}>
          {/* Row 1 */}
          <Grid item xs={2}>
            {tf("Product Name *", productName, setProductName, {
              inputProps: { maxLength: 200 },
            })}
          </Grid>
          <Grid item xs={2}>
            {select(
              "Vendor *",
              vendorRecordId,
              setVendorRecordId,
              vendors.map((vendor) => (
                <MenuItem key={vendor.recordId} value={vendor.recordId}>
                  {vendor.vendorName}
                </MenuItem>
              ))
            )}
          </Grid>
          <Grid item xs={2}>
            {select(
              "Category",
              category,
              setCategory,
              categoryOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))
            )}
          </Grid>
          <Grid item xs={2}>
            {tf("Item Number", sku, setSku, { inputProps: { maxLength: 100 } })}
          </Grid>
          <Grid item xs={2}>
            {tf("UPC", upc, setUpc, { inputProps: { maxLength: 100 } })}
          </Grid>
          <Grid item xs={2}>
            {select(
              "Order Level",
              orderLevel,
              setOrderLevel,
              inventoryOrderLevelList.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))
            )}
          </Grid>

          {/* Row 2 */}
          <Grid item xs={2}>{tf("Cost", cost, setCost, { type: "number" })}</Grid>
          <Grid item xs={2}>
            {tf("Leadtime (days)", leadTimeDays, setLeadTimeDays, {
              type: "number",
            })}
          </Grid>
          <Grid item xs={2}>
            {tf("Burn Rate (units / week)", burnRatePerWeek, setBurnRatePerWeek, {
              type: "number",
            })}
          </Grid>
          <Grid item xs={4}>
            {tf("Description", description, setDescription, {
              inputProps: { maxLength: 1000 },
            })}
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(isActive)}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>

          {/* Row 3 - ENB-584 reorder */}
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={useCalculatedBurnRate}
                  onChange={(e) => setUseCalculatedBurnRate(e.target.checked)}
                />
              }
              label="Use calculated 60-day burn rate for reorder"
            />
          </Grid>
          {reorder && (
            <Grid item xs={8} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Calculated burn rate: {reorder.burnRate60Days} (60 days) /{" "}
                {reorder.burnRate120Days} (120 days) per week | Reorder point:{" "}
                {reorder.reorderStatus === "Not Set" ? "-" : reorder.reorderPoint}
              </Typography>
              <ReorderStatusChip status={reorder.reorderStatus} />
            </Grid>
          )}

          {/* Actions */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button variant="outlined" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Save"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Variants */}
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Typography variant="subtitle2">Variants</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddVariant}
          >
            Add Variant
          </Button>
        </div>
        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Variant</TableCell>
              <TableCell>Item Number</TableCell>
              <TableCell>UPC</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {variants.map((variant, index) => (
              <TableRow key={`variant-${index}`}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={variant.variantName || ""}
                    onChange={(e) =>
                      handleVariantChange(index, "variantName", e.target.value)
                    }
                    placeholder="Red, 500ml, ..."
                    inputProps={{ maxLength: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={variant.sku || ""}
                    onChange={(e) =>
                      handleVariantChange(index, "sku", e.target.value)
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={variant.upc || ""}
                    onChange={(e) =>
                      handleVariantChange(index, "upc", e.target.value)
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={variant.cost ?? ""}
                    onChange={(e) =>
                      handleVariantChange(index, "cost", e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    style={{ color: "red" }}
                    onClick={() => handleRemoveVariant(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {variants.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">
                    No variants. Leave empty if this product has none.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Locations / stock */}
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Typography variant="subtitle2">
            Locations &amp; Stock (total: {totalStock})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddLocation}
          >
            Add Location
          </Button>
        </div>
        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Location</TableCell>
              <TableCell>Variant</TableCell>
              <TableCell>Lot</TableCell>
              <TableCell>Expiration</TableCell>
              <TableCell>Stock Level</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((location, index) => (
              <TableRow key={`location-${index}`}>
                <TableCell>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={location.locationName || ""}
                    onChange={(e) =>
                      handleLocationChange(index, "locationName", e.target.value)
                    }
                  >
                    {locationOptions.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={location.variantName || ""}
                    onChange={(e) =>
                      handleLocationChange(index, "variantName", e.target.value)
                    }
                  >
                    <MenuItem value="">(product)</MenuItem>
                    {variants
                      .filter((variant) => variant.variantName?.trim())
                      .map((variant) => (
                        <MenuItem
                          key={variant.variantName}
                          value={variant.variantName}
                        >
                          {variant.variantName}
                        </MenuItem>
                      ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={location.lotNumber || ""}
                    onChange={(e) =>
                      handleLocationChange(index, "lotNumber", e.target.value)
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    value={location.expirationDate ? location.expirationDate.split("T")[0] : ""}
                    onChange={(e) =>
                      handleLocationChange(index, "expirationDate", e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={location.stockLevel ?? 0}
                    onChange={(e) =>
                      handleLocationChange(index, "stockLevel", e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={location.notes || ""}
                    onChange={(e) =>
                      handleLocationChange(index, "notes", e.target.value)
                    }
                    inputProps={{ maxLength: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    style={{ color: "red" }}
                    onClick={() => handleRemoveLocation(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {locations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    No locations yet. Stock is counted per location, so add one
                    to record what is on hand.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <CommonLoader loading={loading || saving} />
    </div>
  );
};

export default AddEditProduct;
